import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import { httpClient } from "api/httpClient";
import { ModalDialogWithoutButton } from "components/ModalDialog";
import { useSPPScannerContext } from "components/Scanner/GlobalSPPScanner";
import { envSettings } from "core/EnvSettings";
import { OrderStatusMovementAndReservationPallet } from "entities/OrderStatus";
import { ScannerLogContextType } from "entities/ScannerLogContextType";
import React from "react";
import {
	TopToolbar,
	sanitizeListRestProps,
	useListContext,
	useMutation,
	useNotify,
} from "react-admin";
import { toast } from "react-toastify";
import { isInArray } from "utils/general";
import { useNotifyError } from "utils/notifiers/useNotifyError";
import {
	findPalletByCurrentSscc,
	matchPalletByOriginalSscc,
	PALLET_SCAN_MATCH,
} from "utils/scanner/matchPalletByScannedSscc";
import { useGS1BarcodeResolver } from "utils/scanner/useGS1BarcodeResolver";

import { OrderStatus } from "../../../../../entities/OrderStatus";
import { boxPickStatus } from "./Constants";
import { operationModeToggleEnum } from "./OperationModeToggleFilter";
import { ReserveModal } from "./ReserveModal";
import { WarningReservationActionsDialogContent } from "./WarningModal";

const useStyles = makeStyles((theme) => ({
	reserveButton: {
		marginRight: 10,
	},
	reservationMode: {
		"&.MuiButton-contained": {
			backgroundColor: "#f44336",
			color: "white",
			"&:hover": {
				backgroundColor: "#d32f2f",
			},
		},
	},
	palletMovementMode: {
		"&.MuiButton-contained": {
			backgroundColor: "#ff9800",
			color: "white",
			"&:hover": {
				backgroundColor: "#f57c00",
			},
		},
	},
}));

export const PickingListActions = (props) => {
	const {
		className,
		exporter,
		filters,
		onReserve,
		orderId,
		translate,
		selectedPalletsForReservation,
		unselectedReservedPallets,
		palletBoxCounts,
		isReservationMode,
		isSelectedViewMode,
		operationMode,
		actionOnSelectionPallet,
		actionOnBoxMovementPick,
		orderPallets,
		terminalId,
		orderStatus,
		startBoxMovement,
		isSelectingTarget,
		isBoxMovementMode,
		isConfirmBoxMovementMode,
		cancelBoxMovement,
		refreshReservationTotals,
		...rest
	} = props;
	const {
		resource,
		basePath,
		displayedFilters,
		filterValues,
		showFilter,
		data,
		refetch,
		...listContextRest
	} = useListContext(props);
	const notify = useNotify();
	const notifyError = useNotifyError();
	const classes = useStyles();

	const { tryScan } = useGS1BarcodeResolver();

	const { registerScanHandler, registerTargetPage, isConnected, isManualAuthorizationRef } =
		useSPPScannerContext();

	const [isWarningDialogOpened, setIsWarningDialogOpened] = React.useState(false);
	const prevConnectedRef = React.useRef(isConnected);
	const isMovementAndReservationAllowed =
		OrderStatusMovementAndReservationPallet.includes(orderStatus);

	const isScannerMode = operationMode === operationModeToggleEnum[0].value;
	const isScannerReservationActive =
		isReservationMode && (envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE || isScannerMode);
	const isScannerBoxMovementActive =
		isBoxMovementMode && (envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE || isScannerMode);
	const isScannerActive = isScannerReservationActive || isScannerBoxMovementActive;

	// Monitor connection state changes and show notifications (only on target pages, not during manual authorization)
	React.useEffect(() => {
		const autoHideDuration = 10000;

		if (!isScannerActive) return;

		if (isConnected && !prevConnectedRef.current && !isManualAuthorizationRef.current) {
			// Just connected automatically on target page
			notify(`✅ ${translate("scanner.notifications.connected")}`, {
				type: "success",
				autoHideDuration,
			});
		}

		prevConnectedRef.current = isConnected;
	}, [isConnected, isManualAuthorizationRef, isScannerActive, notify, translate]);

	// Helper function to trigger list refetch with current filters preserved
	const refreshList = React.useCallback(async () => {
		try {
			await refetch();
		} catch (error) {
			console.error("Failed to refresh list:", error);
			notify(translate("tab.picking_list.refresh_failed"), "warning");
		}
	}, [refetch, notify, translate]);

	const [mutate, { loading: isSubmitting }] = useMutation(
		{
			type: "create",
			resource: "pallet-box-reservations",
		},
		{
			withDeclarativeSideEffectsSupport: true,
			onSuccess: async () => {
				notify(translate("tab.picking_list.reservation_action_successful"), "success");

				props.onSuccess && props.onSuccess();

				await refreshList();

				if (props.clearSelections) {
					props.clearSelections();
				}
				// Refresh reservation totals after successful reservation
				refreshReservationTotals && refreshReservationTotals();
			},
			onFailure: (error) => {
				notify(error.message, "error");
			},
		}
	);

	const handleCloseDialog = () => {
		setIsWarningDialogOpened(false);
	};

	const getBoxesCountFromPallets = (palletIds) =>
		palletIds.reduce((sum, id) => sum + (palletBoxCounts?.[id] ?? data?.[id]?.quantity ?? 0), 0);

	const reservedPalletsBoxCount = getBoxesCountFromPallets(selectedPalletsForReservation);
	const unreservedPalletsBoxCount = getBoxesCountFromPallets(unselectedReservedPallets);

	const handleSubmitReservationAttempt = () => {
		if (unselectedReservedPallets.length > 0 || selectedPalletsForReservation.length > 0) {
			setIsWarningDialogOpened(true);
		} else {
			onReservationSubmit();
		}
	};

	const handleConfirmUnreserve = () => {
		setIsWarningDialogOpened(false);
		onReservationSubmit();
	};

	const onReservationSubmit = () => {
		mutate({
			payload: {
				data: {
					order_id: orderId,
					reserved_pallets_ids: selectedPalletsForReservation,
					unreserved_pallets_ids: unselectedReservedPallets,
				},
			},
		});
	};

	const handleOnScan = React.useCallback(
		(code) => {
			const started = tryScan(code, async ({ result, error }) => {
				toast.info(translate("scanner.notifications.scanned_success", { code }), {
					autoClose: 2000,
				});
				if (error) {
					// 400 = not a GS1 code; abort(timeout) / 5xx = a real failure
					if (error?.status === 400) {
						notify(translate("tab.picking_list.entity_scan_unsuccess"), "warning");
					} else {
						notify(error.message || translate("tab.picking_list.entity_scan_unsuccess"), "error");
					}
					return;
				}
				const sscc = result?.sscc ?? null;
				const gtin = result?.gtin ?? null;
				const resolvedCode = sscc ?? gtin;
				if (!resolvedCode) {
					notify(translate("tab.picking_list.entity_scan_unsuccess"), "warning");
					return;
				}
				const _filter = encodeURIComponent(JSON.stringify(sscc ? { scanned_sscc: sscc } : { gtin }));
				let json;
				try {
					// await keeps the fetch + reservation inside the single-flight lock
					({ json } = await httpClient(`order/${orderId}/pallets-and-boxes?filter=${_filter}`, {
						method: "GET",
					}));
				} catch (err) {
					notify(err.message, "error");
					return;
				}
				if (json.length === 0) {
					notify(translate("tab.picking_list.entity_scan_unsuccess"), "warning");
					return;
				}
				const targetPalletMatch = matchScannedTargetPallet(json, sscc);
				if (targetPalletMatch.status === PALLET_SCAN_MATCH.AMBIGUOUS) {
					notify(translate("scanner.pallet_sscc_ambiguous"), "warning");
					return;
				}
				if (targetPalletMatch.status !== PALLET_SCAN_MATCH.MATCHED) {
					notify(translate("tab.picking_list.entity_scan_unsuccess"), "warning");
					return;
				}
				const targetPallet = targetPalletMatch.pallet;

				// Check if pallet is loaded into truck
				if (targetPallet.loaded_into_truck) {
					notify(translate("tab.picking_list.pallet_already_loaded"), "warning");
					return;
				}

				// Check if pallet is moved to preparation place
				if (targetPallet.into_preparation_place) {
					notify(translate("tab.picking_list.pallet_already_in_preparation"), "warning");
					return;
				}

				// Check if pallet is reserved for another order
				const isPalletReservedForAnotherOrder =
					targetPallet.reserved_order_id && targetPallet.reserved_order_id !== orderId;

				if (isPalletReservedForAnotherOrder) {
					notify(translate("tab.picking_list.pallet_reserved_for_another_order"), "warning");
					return;
				}

				if (!targetPallet.is_storage_place_confirmed) {
					notify(translate("tab.picking_list.entity_scan_unable_to_reserve"), "warning");
					return;
				}

				if (!isInArray(listContextRest.ids, targetPallet.id)) {
					// Add scanned pallet to data if not present
					data[targetPallet.id] = targetPallet;
					listContextRest.ids.push(targetPallet.id);
					refreshList();
				}
				if (isReservationMode) {
					// Reservation is whole-pallet only: scanning a pallet or any box on it reserves the pallet.
					actionOnSelectionPallet.current(targetPallet);
					notify(translate("tab.picking_list.pallet_scan_success"), "success");
				} else if (isBoxMovementMode) {
					const targetBox = targetPallet.boxes?.find((box) => box.sscc === resolvedCode);
					if (!targetBox) {
						notify(translate("tab.picking_list.box_movement.scan_box_not_pallet"), "warning");
						return;
					}
					const status = actionOnBoxMovementPick.current(targetPallet, targetBox);
					if (status === boxPickStatus.MOVED) {
						notify(translate("tab.picking_list.box_movement.box_already_moved"), "warning");
					} else if (status === boxPickStatus.DUPLICATE) {
						notify(translate("tab.picking_list.box_movement.box_already_picked"), "warning");
					} else if (status === boxPickStatus.ADDED) {
						notify(translate("tab.picking_list.box_movement.box_pick_success"), "success");
					} else {
						notify("no status");
					}
				}
			});
			if (!started) notify(translate("scanner.already_resolving"), "warning");
		},
		[
			tryScan,
			translate,
			orderId,
			listContextRest.ids,
			isReservationMode,
			isBoxMovementMode,
			actionOnSelectionPallet,
			actionOnBoxMovementPick,
			notify,
			data,
			refreshList,
		]
	);

	// Register this page as a target page when scanner functionality is active
	React.useEffect(() => {
		if (!isScannerActive) {
			return undefined;
		}
		const unregisterPage = registerTargetPage("picking-list", {
			contextType: ScannerLogContextType.order,
			contextId: orderId,
			terminalId,
		});
		return unregisterPage;
	}, [isScannerActive, registerTargetPage, orderId, terminalId]);

	// Register scan handler while scanner functionality is active on this page
	React.useEffect(() => {
		if (!isScannerActive) {
			return undefined;
		}
		const unregister = registerScanHandler("picking-list", handleOnScan);
		return unregister;
	}, [handleOnScan, isScannerActive, registerScanHandler]);

	return React.useMemo(
		() => (
			<TopToolbar className={className} {...sanitizeListRestProps(rest)}>
				{isMovementAndReservationAllowed && (
					<>
						{!isReservationMode && (
							<ReserveModal
								orderId={orderId}
								orderPallets={orderPallets}
								buttonText={translate("tab.picking_list.reserve")}
								disabled={isConfirmBoxMovementMode || isSelectingTarget || isBoxMovementMode}
								refreshList={refreshList}
							/>
						)}

						{/* start/cancel reserving */}
						<Button
							variant="contained"
							color={isReservationMode ? undefined : "primary"}
							onClick={onReserve}
							className={`${classes.reserveButton} ${
								isReservationMode ? classes.reservationMode : ""
							}`}
							disabled={isConfirmBoxMovementMode || isSelectingTarget || isBoxMovementMode}
						>
							{isReservationMode
								? translate("osfc.buttons.cancel")
								: translate("tab.picking_list.manual_reserve")}
						</Button>
						{envSettings.REACT_APP_FF_STORAGE_MODULE && !isReservationMode && (
							<Button
								variant="contained"
								color={isSelectingTarget || isBoxMovementMode ? undefined : "primary"}
								onClick={
									isSelectingTarget || isBoxMovementMode ? cancelBoxMovement : startBoxMovement
								}
								className={`${classes.reserveButton} ${
									isSelectingTarget || isBoxMovementMode ? classes.palletMovementMode : ""
								}`}
								disabled={isConfirmBoxMovementMode}
							>
								{isSelectingTarget || isBoxMovementMode
									? translate("osfc.buttons.cancel")
									: translate("tab.picking_list.box_movement.buttons.move_boxes")}
							</Button>
						)}
					</>
				)}

				{/* submit reservation data btn */}
				{isReservationMode && (
					<Button
						variant="contained"
						color="primary"
						onClick={handleSubmitReservationAttempt}
						style={{ marginRight: 10 }}
						disabled={isSelectedViewMode || isSubmitting}
					>
						{translate("osfc.buttons.submit")}
					</Button>
				)}

				{filters &&
					React.cloneElement(filters, {
						resource,
						showFilter,
						displayedFilters,
						filterValues,
						context: "button",
					})}

				<ModalDialogWithoutButton
					isOpen={isWarningDialogOpened}
					handleClose={handleCloseDialog}
					content={
						<WarningReservationActionsDialogContent
							translate={translate}
							unreservedPalletsIds={unselectedReservedPallets}
							reservedPalletsIds={selectedPalletsForReservation}
							reservedBoxesTotal={reservedPalletsBoxCount}
							unreservedBoxesTotal={unreservedPalletsBoxCount}
							handleClose={handleCloseDialog}
							handleConfirmUnreserve={handleConfirmUnreserve}
							isSubmitting={isSubmitting}
						/>
					}
				/>
			</TopToolbar>
		),
		[
			resource,
			displayedFilters,
			filterValues,
			filters,
			isWarningDialogOpened,
			isSubmitting,
			isReservationMode,
			isSelectingTarget,
			isBoxMovementMode,
			isConfirmBoxMovementMode,
		] // eslint-disable-line react-hooks/exhaustive-deps
	);
};

const matchScannedTargetPallet = (pallets, sscc) => {
	if (!sscc) {
		return toMatchedPallet(pallets[0]);
	}
	const current = findPalletByCurrentSscc(pallets, sscc);
	if (current) {
		return toMatchedPallet(current);
	}
	const boxOwner = pallets.find((pallet) => pallet.boxes?.some((box) => box.sscc === sscc));
	if (boxOwner) {
		return toMatchedPallet(boxOwner);
	}
	return matchPalletByOriginalSscc(pallets, sscc);
};

const toMatchedPallet = (pallet) => ({ status: PALLET_SCAN_MATCH.MATCHED, pallet });
